import { FOUNDER_CALL_LABEL, FOUNDER_CALL_URL } from '../founderCall';
import type { FeatureContent } from './types';

export const aiInterviewNotes: FeatureContent = {
  slug: 'ai-interview-notes',
  path: '/features/ai-interview-notes',
  scorecardConnector:
    'The record layer of the candidate scorecard: the structured summary you keep after the interview.',
  audience: 'company',
  icon: 'Bot',
  name: 'Note Taker',
  stage: 5,
  navLabel: 'AI Interview Notetaker',
  navDescription: 'A bot joins the call and turns it into a decision-ready record.',
  primaryKeyword: 'AI interview notetaker',
  seo: {
    title: 'AI Interview Notetaker for Meet, Teams & Zoom | Talenture.ai',
    description:
      'AI interview notetaker that joins your Google Meet, Teams, or Zoom calls, transcribes with speaker labels, and returns a structured summary with fit insights.',
    keywords: [
      'AI interview notetaker',
      'AI interview notes',
      'interview transcription',
      'interview notetaker Meet Teams Zoom',
      'recruiting note taker',
      'interview summary AI',
    ],
    ogTitle: 'AI Interview Notetaker for Meet, Teams and Zoom | Talenture.ai',
    ogDescription:
      'A bot joins your interview on Google Meet, Teams, or Zoom, transcribes with speaker labels, and returns a structured summary with fit insights and action items. Be present in the room.',
  },
  hero: {
    eyebrow: 'Stage 5 of the hiring mission · Capture the interview',
    whereThisFits:
      'Every interview becomes a structured record, so your decision rests on signal, not memory.',
    h1: 'AI Interview Notetaker: Be Present. Let Talenture Capture the Record.',
    painOpener: 'You can take notes or be present. Not both.',
    subhead:
      'A note-taking bot joins your Google Meet, Microsoft Teams, or Zoom interview, captures every word with speaker labels, and turns it into a structured, decision-ready record while you focus on the candidate.',
    worksWithAts: true,
    primaryCta: { label: 'See a sample scorecard', href: '/sample-scorecard' },
    secondaryCta: { label: FOUNDER_CALL_LABEL, href: FOUNDER_CALL_URL },
  },
  definition: {
    heading: 'What is an AI interview notetaker?',
    body: 'An AI interview notetaker is a bot that joins your video interview, transcribes the conversation with speaker labels, and produces a structured summary. Talenture turns each interview into an executive summary, fit insights, and action items, so you stay present in the conversation and still keep a complete, searchable record.',
  },
  sections: [
    {
      icon: 'Video',
      title: 'Joins the call automatically',
      body: 'The Talenture Notes bot joins your scheduled interview on the platforms you already use, with accuracy-prioritised transcription in real time.',
      bullets: [
        'Works on Google Meet, Microsoft Teams, and Zoom',
        'Speaker-separated transcript with names and timestamps',
        'Accuracy-prioritised, real-time capture',
      ],
    },
    {
      icon: 'FileSearch',
      title: 'From transcript to decision',
      body: 'A raw transcript is data. Talenture turns it into something you can act on: a summary, the signals that matter for fit, and clear next steps.',
      bullets: [
        'Executive summary of the conversation',
        'Key insights on candidate fit',
        'Action items with owners',
        'Sentiment across the interview',
      ],
    },
    {
      icon: 'ShieldCheck',
      title: 'Retention you can defend',
      body: 'Raw transcripts are deleted after roughly 90 days by default, configurable to your policy, while the structured notes are retained, so you keep the decision record without hoarding sensitive recordings.',
    },
  ],
  visual: {
    component: 'InterviewRecapScreen',
    url: 'app.talenture.ai/candidates/mira-n/interview-notes',
    caption:
      'The whole recap: summary, key insights, sentiment, action items, follow-ups and the raw transcript underneath them.',
  },
  supportingVisuals: [
    {
      component: 'CandidateNextStepsScreen',
      url: 'app.talenture.ai/candidates/mira-n/next-steps',
      heading: 'The notes turn into moves',
      caption:
        'What the record says to do next, ordered by urgency and sitting on the same candidate as the recap.',
    },
  ],
  howTo: {
    heading: 'How AI interview notes work',
    intro: 'Three steps from a live interview to a decision-ready record.',
    steps: [
      {
        title: 'Join the scheduled call',
        body: 'The Talenture Notes bot joins your Google Meet, Microsoft Teams, or Zoom interview automatically.',
      },
      {
        title: 'Capture the conversation',
        body: 'The bot transcribes the interview in real time with speaker names and timestamps, so the record reflects what was actually said.',
      },
      {
        title: 'Turn the transcript into a decision record',
        body: 'Talenture returns an executive summary, fit insights, sentiment, and action items with owners, while raw transcripts follow your retention policy.',
      },
    ],
  },
  proofPoints: [
    { value: '3', label: 'platforms: Google Meet, Teams, and Zoom' },
    { value: '~90 days', label: 'default transcript retention, configurable' },
    { value: 'Speaker-labelled', label: 'transcript with timestamps' },
  ],
  faq: [
    {
      question: 'Which meeting platforms are supported?',
      answer:
        'Google Meet, Microsoft Teams, and Zoom. The Talenture Notes bot joins the scheduled interview automatically and transcribes in real time with speaker separation, so you do not have to record or take notes manually.',
    },
    {
      question: 'What do I get after the interview?',
      answer:
        'A speaker-labelled transcript plus a structured analysis: an executive summary, key insights on candidate fit, action items with owners, and sentiment across the conversation. The output is built to support a decision, not just to store words.',
    },
    {
      question: 'How long are recordings and transcripts kept?',
      answer:
        'Raw transcripts are deleted after roughly 90 days by default, and this is configurable to match your retention policy. The structured AI notes are retained so you keep the decision record without holding sensitive raw data longer than needed.',
    },
    {
      question: 'Does the bot replace the interviewer?',
      answer:
        'No. The bot handles capture and analysis so the interviewer can be fully present with the candidate. AI handles the notes, humans make the decision.',
    },
  ],
  relatedSlugs: ['interview-scheduling', 'ai-mock-interviews', 'candidate-feedback'],
};
